<template>
  <div
    :class="[
      'p-ripple-wrapper',
      className,
      {
        'p-ripple-disabled': !enabled,
        'p-ripple-responsive': responsive,
        'p-ripple-rtl': dir === 'rtl'
      }
    ]"
    :style="[
      style,
      {
        '--ripple-color': color,
        '--ripple-duration': `${duration}ms`
      }
    ]"
    :role="enabled ? 'button' : undefined"
    :tabindex="enabled ? 0 : undefined"
    :aria-label="ariaLabel"
    v-ripple="rippleConfig"
  >
    <slot></slot>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Ripple from 'primevue/ripple'
import type { MOPRippleProps } from './types'

export default defineComponent({
  name: 'MOPRipple',
  directives: {
    ripple: Ripple
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<MOPRippleProps>(), {
  enabled: true,
  className: undefined,
  style: undefined,
  dir: 'ltr',
  responsive: true,
  ariaLabel: 'Interactive element with ripple effect',
  color: 'currentColor',
  duration: 400,
  centered: false
})

const rippleConfig = computed(() => ({
  disabled: !props.enabled,
  centered: props.centered
}))
</script>

<style lang="scss">
.p-ripple-wrapper {
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(33, 150, 243, 0.25);
  }

  &.p-ripple-disabled {
    cursor: default;
    pointer-events: none;
  }

  &.p-ripple-responsive {
    @media screen and (max-width: 576px) {
      .p-ink {
        transform: scale(0.8);
      }
    }
  }

  &.p-ripple-rtl {
    direction: rtl;
    .p-ink {
      transform-origin: right center;
    }
  }

  .p-ink {
    position: absolute;
    background: var(--ripple-color, currentColor);
    border-radius: 50%;
    pointer-events: none;
    opacity: 0.3;
    transform: scale(0);
    animation: p-ripple var(--ripple-duration, 400ms) cubic-bezier(0.4, 0, 0.2, 1);
  }
}

@keyframes p-ripple {
  to {
    transform: scale(2.5);
    opacity: 0;
  }
}

// Theme integration
:root {
  --ripple-bg: currentColor;
  --ripple-opacity: 0.3;
}

.p-ripple {
  &-primary {
    --ripple-bg: var(--primary-color, #2196F3);
  }

  &-secondary {
    --ripple-bg: var(--secondary-color, #607D8B);
  }

  &-success {
    --ripple-bg: var(--success-color, #4CAF50);
  }

  &-info {
    --ripple-bg: var(--info-color, #2196F3);
  }

  &-warning {
    --ripple-bg: var(--warning-color, #FFC107);
  }

  &-danger {
    --ripple-bg: var(--danger-color, #F44336);
  }
}
</style>
